import { useCallback, useState, type ReactNode } from 'react'
import { messageErreur } from '../../onboarding/erreurs'
import {
  COULEUR_STATUT,
  LIBELLE_STATUT,
  type ActionStatut,
  type ActionStaff,
  type Etape,
} from '../../onboarding/etapes'
import type { EvenementJournal } from '../../onboarding/types'
import { dateComplete } from '../Format'

/**
 * Briques communes des fiches de candidature (livreur, commerçant) : pastille
 * de statut, stepper, prochaine action, décision motivée, journal, et le petit
 * hook qui porte l'état « en cours / erreur » d'une action asynchrone.
 */

/** État d'une action asynchrone : l'erreur est TOUJOURS une phrase lisible. */
export function useAction() {
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const lancer = useCallback(async (fn: () => Promise<string | void>): Promise<boolean> => {
    setOccupe(true)
    setErreur(null)
    setInfo(null)
    try {
      const message = await fn()
      if (typeof message === 'string') setInfo(message)
      return true
    } catch (e) {
      setErreur(messageErreur(e))
      return false
    } finally {
      setOccupe(false)
    }
  }, [])

  return { occupe, erreur, info, lancer, signaler: setErreur }
}

export function BadgeStatut({ statut }: { statut: string }) {
  return <span className={`badge badge-${COULEUR_STATUT[statut] ?? 'gray'}`}>{LIBELLE_STATUT[statut] ?? statut}</span>
}

const Coche = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)
const Croix = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

export function Stepper({ etapes }: { etapes: Etape[] }) {
  return (
    <ol className="stepper" aria-label="Étapes du parcours">
      {etapes.map((e, i) => (
        <li key={e.cle} className={`step step-${e.etat}`}>
          <span className="step-pt">{e.etat === 'fait' ? <Coche /> : e.etat === 'arret' ? <Croix /> : i + 1}</span>
          <span className="step-lb">{e.libelle}</span>
        </li>
      ))}
    </ol>
  )
}

export function ProchaineAction({ action }: { action: ActionStaff }) {
  return (
    <div className={`dos-action${action.pourStaff ? '' : ' attente'}`}>
      {action.pourStaff ? 'À faire : ' : 'En attente : '}
      {action.libelle}
    </div>
  )
}

export function Bloc({ titre, droite, children }: { titre: string; droite?: ReactNode; children: ReactNode }) {
  return (
    <div className="dos-bloc">
      <h4>
        {titre}
        {droite && <span style={{ marginLeft: 'auto', fontWeight: 500 }}>{droite}</span>}
      </h4>
      {children}
    </div>
  )
}

export const Erreur = ({ texte }: { texte: string | null }) => (texte ? <div className="dos-erreur">{texte}</div> : null)
export const Info = ({ texte }: { texte: string | null }) => (texte ? <div className="dos-ok">{texte}</div> : null)

/**
 * Décision de statut : activer, refuser, suspendre, rouvrir… Un motif est
 * exigé AVANT l'appel pour refuser/suspendre (la base l'exige aussi). Refuser
 * ou suspendre passe par une confirmation : la décision touche une personne.
 */
export function BlocDecision({
  actions,
  surDecider,
}: {
  actions: ActionStatut[]
  surDecider: (vers: string, motif: string) => Promise<void>
}) {
  const [motif, setMotif] = useState('')
  const { occupe, erreur, lancer, signaler } = useAction()
  const besoinMotif = actions.some((a) => a.motifRequis)

  if (actions.length === 0) return <p className="dos-note">Aucune décision n'est possible à cette étape.</p>

  async function decider(a: ActionStatut) {
    if (a.motifRequis && !motif.trim()) {
      signaler('Un motif est obligatoire pour cette décision.')
      return
    }
    if (a.ton === 'danger' && !window.confirm(`${a.libelle} ce dossier ?\n\nMotif : ${motif.trim()}`)) return
    const ok = await lancer(() => surDecider(a.vers, motif))
    if (ok) setMotif('')
  }

  return (
    <>
      {besoinMotif && (
        <textarea
          rows={2}
          placeholder="Motif (obligatoire pour refuser ou suspendre)"
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
        />
      )}
      <div className="dos-champs">
        {actions.map((a) => (
          <button
            key={a.vers}
            className={`btn btn-sm ${a.ton === 'principal' ? 'btn-accent' : a.ton === 'danger' ? 'btn-danger-sm' : 'btn-outline'}`}
            disabled={occupe}
            onClick={() => void decider(a)}
          >
            {a.libelle}
          </button>
        ))}
      </div>
      <Erreur texte={erreur} />
    </>
  )
}

export function Journal({ evenements }: { evenements: EvenementJournal[] }) {
  if (evenements.length === 0) return <p className="dos-note">Aucun changement de statut enregistré.</p>
  return (
    <ul className="dos-liste">
      {evenements.map((e) => (
        <li key={e.id}>
          <span>
            {e.de ? `${LIBELLE_STATUT[e.de] ?? e.de} → ` : ''}
            <b>{LIBELLE_STATUT[e.vers] ?? e.vers}</b>
          </span>
          {e.motif && <span className="dos-note">« {e.motif} »</span>}
          <span className="quand">
            {e.auteur ? `${e.auteur} · ` : ''}
            {dateComplete(e.date)}
          </span>
        </li>
      ))}
    </ul>
  )
}
